import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { articles } from '@/mocks/articles';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import { useSEO } from '@/hooks/useSEO';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.id !== article.id && a.categorySlug === article.categorySlug)
      .slice(0, 3);
  }, [article]);

  useSEO({
    title: article ? `${article.title} | 炽热之夜电影资讯站` : '文章未找到 | 炽热之夜电影资讯站',
    description: article?.excerpt ?? '浏览《炽热之夜》菲律宾电影相关文章',
    keywords: article?.tags?.join(',') ?? '炽热之夜,菲律宾电影',
    canonicalUrl: `https://chirezhizhiye.ph/article/${slug}`,
    ogTitle: article?.title ?? '炽热之夜电影资讯站',
    ogDescription: article?.excerpt ?? '',
    ogType: 'article',
    ogImage: article?.image ?? '',
  });

  if (!article) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <h2 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h2>
        <Link to="/" className="text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
          返回首页
        </Link>
      </div>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '炽热之夜电影资讯站',
    },
    url: `https://chirezhizhiye.ph/article/${slug}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <JsonLd data={articleJsonLd} />

      <article>
        <section className="w-full px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.category, href: `/category/${article.categorySlug}` },
                { label: article.title },
              ]}
            />

            <div className="mt-6">
              <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-md mb-4 whitespace-nowrap">
                {article.category}
              </span>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读时间 {article.readTime}
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-0 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden -mt-4 mb-8">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 菲律宾电影资讯`}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-8 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-foreground-500 whitespace-nowrap">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-800 text-xs rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} article={related} />
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </>
  );
}